import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetch } from '../../../../hooks/use-fetch';
import { BASE_ADMIN_API_URL } from '../../../../config';
import gridDataFr from './data-fr.json';
import gridDataEn from './data-en.json';

const GridDataContext = createContext();

export function GridDataProvider({ children }) {
    const { i18n } = useTranslation();
    const fetchWithAuth = useFetch();
    const currentLang = i18n.language?.substring(0, 2) || 'fr';

    const [nodes, setNodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasCustomConfig, setHasCustomConfig] = useState(false);

    // Select the appropriate default data based on current language
    const getDefaultGridData = useCallback(() => {
        switch (currentLang) {
            case 'en':
                return gridDataEn;
            case 'fr':
            default:
                return gridDataFr;
        }
    }, [currentLang]);

    // Load configuration from API
    useEffect(() => {
        let cancelled = false;

        async function loadConfig() {
            setLoading(true);
            try {
                const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/applications/GRID/configuration`);
                if (!response.ok) throw new Error('Failed to load configuration');

                const config = await response.json();

                if (!cancelled) {
                    // If config exists and has grids, use the first grid
                    if (config && config.grids && config.grids.length > 0) {
                        setNodes(JSON.parse(JSON.stringify(config.grids[0].root)));
                        setHasCustomConfig(true);
                    } else {
                        // Use default language-based grid
                        setNodes(JSON.parse(JSON.stringify(getDefaultGridData().grid.root)));
                        setHasCustomConfig(false);
                    }
                }
            } catch (e) {
                console.error('Failed to load grid configuration', e);
                if (!cancelled) {
                    // Fallback to default on error
                    setNodes(JSON.parse(JSON.stringify(getDefaultGridData().grid.root)));
                    setHasCustomConfig(false);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadConfig();
        return () => { cancelled = true; };
    }, [fetchWithAuth, getDefaultGridData]);

    // Update nodes when language changes (only if no custom config)
    useEffect(() => {
        if (!hasCustomConfig && !loading) {
            setNodes(JSON.parse(JSON.stringify(getDefaultGridData().grid.root)));
        }
    }, [currentLang, hasCustomConfig, loading, getDefaultGridData]);

    // Save configuration to API
    const saveConfiguration = useCallback(async (updatedNodes) => {
        try {
            const config = {
                version: 1,
                grids: [
                    {
                        id: 'grid_01',
                        profile_id: 'profile_01',
                        label: currentLang === 'en' ? 'Daily grid' : 'Grille quotidienne',
                        root: updatedNodes,
                    },
                ],
            };

            const response = await fetchWithAuth(
                `${BASE_ADMIN_API_URL}/applications/GRID/configuration`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(config),
                }
            );

            if (!response.ok) throw new Error('Failed to save configuration');

            setNodes(JSON.parse(JSON.stringify(updatedNodes)));
            setHasCustomConfig(true);

            return true;
        } catch (e) {
            console.error('Failed to save grid configuration', e);
            return false;
        }
    }, [fetchWithAuth, currentLang]);

    return (
        <GridDataContext.Provider value={{ nodes, setNodes, saveConfiguration, loading }}>
            {children}
        </GridDataContext.Provider>
    );
}

export function useGridData() {
    return useContext(GridDataContext);
}
